import { useCallback, useEffect, useRef, useState } from 'react'

import { Product, Product2 } from '@/models/Product'
import fetchProductGroup from '@/utils/api/fetchProductGroup'
import transformProductInventorySet from '@/utils/helpers/transformProductInventorySet'

export interface PaginationParams {
  product_id: number
  vendor_id: number
  page_size?: number
  page?: number
}

export const useProductGroup = (
  initialParams: PaginationParams = {
    product_id: 0,
    vendor_id: 0,
    page: 1,
    page_size: 10,
  },
) => {
  const [items, setItems] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null)

  const nextPageUrlRef = useRef<string | null>(null)
  const loadingRef = useRef(false)

  useEffect(() => {
    nextPageUrlRef.current = nextPageUrl
  }, [nextPageUrl])

  useEffect(() => {
    loadingRef.current = loading
  }, [loading])

  const fixNextPageUrl = useCallback((url: string | null): string | null => {
    if (!url) return null
    return url.replace(/^http:\/\//, 'https://')
  }, [])

  const formatAllProducts = useCallback((products: Product2[]): Product[] => {
    return products.map((product) => transformProductInventorySet(product))
  }, [])

  const loadFirstPage = useCallback(async () => {
    if (!initialParams.product_id || !initialParams.vendor_id) return
    if (loadingRef.current) return

    setLoading(true)
    setError(null)

    try {
      const res = await fetchProductGroup(initialParams)

      if (res?.products) {
        const products = formatAllProducts(res.products)
        setItems(products)
        setNextPageUrl(fixNextPageUrl(res.next ?? null))
      } else {
        setItems([])
        setNextPageUrl(null)
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setError(err?.message || 'Unknown error')
        console.error('useProductGroup error:', err)
      }
    } finally {
      setLoading(false)
    }
  }, [
    initialParams.product_id,
    initialParams.vendor_id,
    initialParams.page_size,
    formatAllProducts,
    fixNextPageUrl,
  ])

  const loadNext = useCallback(async () => {
    const url = nextPageUrlRef.current
    if (!url || loadingRef.current) return

    setLoading(true)

    try {
      const res = await fetchProductGroup({ url } as any)

      if (res?.products) {
        const products = formatAllProducts(res.products)
        setItems((prev) => [...prev, ...products])
        setNextPageUrl(fixNextPageUrl(res.next ?? null))
      } else {
        setNextPageUrl(null)
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('useProductGroup error:', err)
        setError(err?.message || 'Unknown error')
      }
    } finally {
      setLoading(false)
    }
  }, [formatAllProducts, fixNextPageUrl])

  useEffect(() => {
    setItems([])
    setNextPageUrl(null)

    if (initialParams.product_id && initialParams.vendor_id) {
      loadFirstPage()
    }
  }, [
    initialParams.product_id,
    initialParams.vendor_id,
    initialParams.page_size,
    loadFirstPage,
  ])

  return {
    items,
    loadNext,
    loading,
    error,
    hasMore: !!nextPageUrl,
    reload: loadFirstPage,
  }
}
